import AddIcon from "@atlaskit/icon/core/add";
import ArchiveIcon from "@atlaskit/icon/core/archive-box";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import ChangesIcon from "@atlaskit/icon/core/changes";
import CommentIcon from "@atlaskit/icon/core/comment";
import CrossCircleIcon from "@atlaskit/icon/core/cross-circle";
import EditIcon from "@atlaskit/icon/core/edit";
import EyeOpenIcon from "@atlaskit/icon/core/eye-open";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import OnCallIcon from "@atlaskit/icon/core/on-call";
import PersonAddIcon from "@atlaskit/icon/core/person-add";
import RoadmapIcon from "@atlaskit/icon/core/roadmap";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import TagIcon from "@atlaskit/icon/core/tag";
import {token} from "@atlaskit/tokens";
import type {ReactNode} from "react";
import {ScheduleCallIcon, WhatsappIcon} from "~/components/svgs";
import {resolveDesignTokenByName} from "~/utilities/resolveDesignTokenByName";
import type {Cta} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";

/** Fallback only when a CTA omits `label` — never override a configured label. */
export const CONFIG_CTA_ACTION_LABELS: Partial<Record<NonNullable<Cta["tableAction"]>, string>> = {
    scheduleCall: "Schedule a call",
    disposition: "Dispose",
    logCall: "Log a call",
    notes: "Notes",
    edit: "Edit",
    view: "View",
    profile: "profile",
    archive: "Archive",
    cancelCall: "Cancel call",
};

export type ConfigCtaAppearanceDefaults = {
    iconColorToken?: string;
    textColorToken?: string;
};

export function toConfigCtaAppearanceDefaults(
    appearance: {iconColorToken: string | null; textColorToken: string | null} | null | undefined,
): ConfigCtaAppearanceDefaults | undefined {
    if (appearance === null || appearance === undefined) {
        return undefined;
    }
    return {
        iconColorToken: appearance.iconColorToken ?? undefined,
        textColorToken: appearance.textColorToken ?? undefined,
    };
}

export type ConfigCtaColorContext = "footer" | "inline";

function resolveConfigDesignToken(tokenName: string | undefined, fallback: string): string {
    if (tokenName === undefined || tokenName.trim() === "") {
        return fallback;
    }
    const trimmed = tokenName.trim();
    if (trimmed.startsWith("var(") || trimmed.startsWith("#")) {
        return trimmed;
    }
    const resolved = resolveDesignTokenByName(trimmed);
    return resolved === "" ? fallback : resolved;
}

export function resolveConfigAppearanceToken(tokenName: string | undefined, fallback: string): string {
    return resolveConfigDesignToken(tokenName, fallback);
}

export function resolveConfigCtaIconColor(
    cta: Cta,
    options: {
        isActive?: boolean;
        isDisabled?: boolean;
        context?: ConfigCtaColorContext;
        appearanceDefaults?: ConfigCtaAppearanceDefaults;
    } = {},
): string {
    if (options.isDisabled === true) {
        return token("color.icon.disabled");
    }
    const configured = cta.iconColorToken ?? options.appearanceDefaults?.iconColorToken;
    if (configured !== undefined) {
        return resolveConfigDesignToken(configured, token("color.icon.brand"));
    }
    if (options.isActive === true) {
        return token("color.icon.brand");
    }
    if (options.context === "footer") {
        return token("color.icon.brand");
    }
    return token("color.icon.subtle");
}

export function resolveConfigCtaTextColor(
    cta: Cta,
    options: {
        isActive?: boolean;
        isDisabled?: boolean;
        context?: ConfigCtaColorContext;
        appearanceDefaults?: ConfigCtaAppearanceDefaults;
    } = {},
): string {
    if (options.isDisabled === true) {
        return token("color.text.disabled");
    }
    const configured = cta.textColorToken ?? options.appearanceDefaults?.textColorToken;
    if (configured !== undefined) {
        return resolveConfigDesignToken(configured, token("color.text.subtle"));
    }
    if (options.isActive === true) {
        return token("color.text.brand");
    }
    return token("color.text.subtle");
}

export function configCtaActionIcon(
    tableAction: NonNullable<Cta["tableAction"]>,
    color: React.ComponentProps<typeof EditIcon>["color"],
): ReactNode {
    switch (tableAction) {
        case "scheduleCall":
            return <ScheduleCallIcon color={color} />;
        case "disposition":
            return <RoadmapIcon label="" size="small" color={color} />;
        case "logCall":
            return <OnCallIcon label="" size="small" color={color} />;
        case "notes":
            return <CommentIcon label="" size="small" color={color} />;
        case "edit":
            return <EditIcon label="" size="small" color={color} />;
        case "view":
            return <EyeOpenIcon label="" size="small" color={color} />;
        case "profile":
            return <LinkExternalIcon label="" size="small" color={color} />;
        case "archive":
            return <ArchiveIcon label="" size="small" color={color} />;
        case "cancelCall":
            return <CrossCircleIcon label="" size="small" color={color} />;
        case "create":
            return <LinkExternalIcon label="" size="small" color={color} />;
    }
}

export function renderConfigCtaIcon(cta: Cta, color: string): ReactNode {
    const iconColor = color as React.ComponentProps<typeof EditIcon>["color"];
    if (cta.action.type === "command" && cta.action.commandId === "workspace.row.whatsapp") {
        return <WhatsappIcon color={color} size={24} />;
    }
    if (cta.tableAction !== null) {
        const fromTableAction = configCtaActionIcon(cta.tableAction, iconColor);
        if (fromTableAction !== undefined) {
            return fromTableAction;
        }
    }
    if (cta.icon === "disposition") {
        return <RoadmapIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "notes") {
        return <CommentIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "schedule") {
        return <ScheduleCallIcon color={color} />;
    }
    if (cta.icon === "calendar") {
        return <CalendarIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "edit") {
        return <EditIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "create") {
        return <AddIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "sales") {
        return <TagIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "view") {
        return <EyeOpenIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "archive") {
        return <ArchiveIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "phone" || cta.icon === "logCall") {
        return <OnCallIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "cancel" || cta.tableAction === "cancelCall") {
        return <CrossCircleIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "inviteUser") {
        return <PersonAddIcon color={iconColor} label="" size="small" />;
    }
    if (cta.icon === "transfer") {
        return <ChangesIcon color={iconColor} label="" size="small" />;
    }
    return <ShowMoreHorizontalIcon color={iconColor} label="" size="small" />;
}

/** Always prefer the CTA's configured `label`; no production-label remaps. */
export function configCtaDisplayLabel(cta: Cta): string {
    const trimmed = cta.label.trim();
    if (trimmed !== "") {
        return trimmed;
    }
    if (cta.tableAction === null) {
        return cta.id;
    }
    return CONFIG_CTA_ACTION_LABELS[cta.tableAction] ?? cta.id;
}
